// src/components/ClassAudiencePicker.js
// Admin-only control for the class visibility allowlist (`visibleToUserIds`).
//
// Unchecked  ⇒ emits []  ⇒ class is PUBLIC (today's behavior).
// Checked    ⇒ shows a filterable roster (admin-only /api/users); emits the
//              selected Clerk user IDs.
//
// Defined at module scope (NOT inside another component) to avoid the inner-
// component remount/focus pitfall. It mounts fresh each time the create/edit
// modal opens, so initializing `enabled` from the incoming value once is safe.

import React, { useEffect, useRef, useState } from 'react';

function ClassAudiencePicker({ getToken, value = [], onChange }) {
    const selected = Array.isArray(value) ? value : [];
    const [enabled, setEnabled] = useState(selected.length > 0);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [filter, setFilter] = useState('');
    const fetchedRef = useRef(false);

    // Fetch the full roster once, the first time the picker is enabled.
    useEffect(() => {
        if (!enabled || fetchedRef.current) return;
        fetchedRef.current = true;
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError('');
            try {
                const token = getToken ? await getToken() : null;
                const res = await fetch('/api/users', {
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = await res.json();
                if (!cancelled) setUsers(Array.isArray(data.users) ? data.users : []);
            } catch (e) {
                if (!cancelled) {
                    setError('Could not load users — try reopening the form.');
                    fetchedRef.current = false; // allow a retry on next enable
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [enabled, getToken]);

    const toggleEnabled = () => {
        const next = !enabled;
        setEnabled(next);
        if (!next) onChange([]); // turning the gate off restores PUBLIC visibility
    };

    const toggleUser = (id) => {
        if (selected.includes(id)) onChange(selected.filter((x) => x !== id));
        else onChange([...selected, id]);
    };

    const term = filter.trim().toLowerCase();
    const visibleUsers = term
        ? users.filter((u) => `${u.firstName || ''} ${u.lastName || ''} ${u.email || ''}`.toLowerCase().includes(term))
        : users;

    const labelStyle = { fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, color: 'var(--mom-charcoal)', marginBottom: 6, display: 'block' };

    return (
        <div>
            <label style={labelStyle}>Visibility (optional)</label>
            <button type="button" onClick={toggleEnabled}
                style={{
                    padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer',
                    fontFamily: 'var(--mom-font-sans)',
                    background: enabled ? 'var(--mom-primary)' : 'var(--mom-cream-dark)',
                    color: enabled ? 'var(--mom-white)' : 'var(--mom-charcoal)',
                    transition: 'all 0.15s ease',
                }}>
                Limit who can see this class
            </button>
            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: 'var(--mom-warm-gray)', marginTop: 4 }}>
                {enabled
                    ? 'Only the selected people (and you) will see this class anywhere in the app.'
                    : 'Off — this class is visible to everyone with access (normal behavior).'}
            </p>

            {enabled && (
                <div style={{ marginTop: 8, border: '1.5px solid var(--mom-cream-dark)', borderRadius: 12, padding: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, gap: 8 }}>
                        <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 700, color: 'var(--mom-charcoal)' }}>
                            {selected.length} selected
                        </span>
                        <input
                            type="text"
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            placeholder="Search name or email"
                            className="mom-input"
                            style={{ flex: 1, maxWidth: 220, fontSize: 12, padding: '6px 10px' }}
                        />
                    </div>
                    {loading && (
                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: 'var(--mom-warm-gray)', margin: '6px 0' }}>Loading people…</p>
                    )}
                    {error && (
                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: 'var(--mom-primary-dark)', margin: '6px 0' }}>{error}</p>
                    )}
                    {!loading && !error && (
                        <div style={{ maxHeight: 200, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {visibleUsers.length === 0 && (
                                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: 'var(--mom-warm-gray)', margin: '6px 0' }}>No matching people.</p>
                            )}
                            {visibleUsers.map((u) => {
                                const isSel = selected.includes(u.id);
                                const name = `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || u.id;
                                return (
                                    <button key={u.id} type="button" onClick={() => toggleUser(u.id)}
                                        style={{
                                            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                                            padding: '8px 10px', borderRadius: 8, cursor: 'pointer', textAlign: 'left',
                                            border: 'none', fontFamily: 'var(--mom-font-sans)',
                                            background: isSel ? 'rgba(195,55,100,0.10)' : 'transparent',
                                        }}>
                                        <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--mom-charcoal)' }}>{name}</span>
                                            {u.email && name !== u.email && (
                                                <span style={{ fontSize: 11, color: 'var(--mom-warm-gray)', marginLeft: 8 }}>{u.email}</span>
                                            )}
                                            {u.classSubscription && (
                                                <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--mom-primary)', marginLeft: 8, letterSpacing: '0.04em' }}>SUB</span>
                                            )}
                                        </span>
                                        <span style={{
                                            width: 18, height: 18, borderRadius: 5, flexShrink: 0,
                                            border: isSel ? 'none' : '1.5px solid var(--mom-cream-dark)',
                                            background: isSel ? 'var(--mom-primary)' : 'transparent',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            color: '#fff', fontSize: 12, fontWeight: 800,
                                        }}>{isSel ? '✓' : ''}</span>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default ClassAudiencePicker;
